import { type ReactElement, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useSignin } from '@/features/auth/hooks/useAuthMutations';
import { useAuthStore } from '@/stores/auth.store';
import { AuthCardLayout } from './../components/AuthCardLayout';
import { AuthForm } from './../components/AuthForm';

const loginSchema = z.object({
  email: z.string().email('Ingresa un correo válido'),
  password: z.string().min(1, 'La contraseña es obligatoria'),
});

type LoginValues = { email: string; password: string };

const LOGIN_FIELDS = [
  {
    name: 'email' as const,
    def: {
      name: 'email',
      label: 'Correo electrónico',
      type: 'email' as const,
      autoComplete: 'email',
      required: true,
      inputMode: 'email' as const,
      placeholder: 'usuario@empresa.com',
    },
  },
  {
    name: 'password' as const,
    def: {
      name: 'password',
      label: 'Contraseña',
      type: 'password' as const,
      autoComplete: 'current-password',
      required: true,
    },
  },
];

export function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const signin = useSignin();
  const token = useAuthStore((s) => s.token);
  const hydrated = useAuthStore((s) => s._hasHydrated);
  const [rememberMe, setRememberMe] = useState(true);

  // If the user is already signed in (token in localStorage), the
  // /me roundtrip on mount via useInitAuth will refresh the user and
  // we route them home. Guarded by `hydrated` so we don't redirect
  // before the persistence layer has had a chance to rehydrate.
  useEffect(() => {
    if (!hydrated) return;
    if (token) {
      const target = safeRedirectTarget(params.get('redirect'));
      navigate(target, { replace: true });
    }
  }, [hydrated, token, params, navigate]);

  return (
    <AuthCardLayout
      title="Inicia sesión"
      subtitle="Accede al panel de tu organización"
      footer={
        <span>
          ¿No podés entrar?{' '}
          <Link
            to="/forgot-password"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Restablece tu contraseña
          </Link>
        </span>
      }
    >
      <AuthForm<LoginValues>
        schema={loginSchema}
        fields={LOGIN_FIELDS}
        defaults={{ email: '', password: '' }}
        submitting={signin.isPending}
        submitLabel="Entrar"
        onValidSubmit={async (values) => {
          await signin.mutateAsync({ ...values, rememberMe });
          const target = safeRedirectTarget(params.get('redirect'));
          navigate(target, { replace: true });
        }}
        serverError={signin.isError ? 'No fue posible validar tus credenciales' : null}
      >
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-border text-primary accent-primary"
          />
          Recordarme por 90 días
        </label>
      </AuthForm>
    </AuthCardLayout>
  );
}

/**
 * Validate the `?redirect=` target to avoid open-redirect bugs AND
 * the redirect-accumulation loop that the previous implementation
 * suffered when an old token was sitting in localStorage.
 *
 * Only same-origin paths (start with `/`, no protocol) are accepted.
 * Absolute URLs, protocol-relative URLs (`//foo`), AND any path
 * pointing back at /login are rejected — the /login case is the
 * critical one because every 401 from a stale token appended another
 * `/login?redirect=…` layer to the URL, producing the
 * `/login?redirect=%2Flogin%3Fredirect%3D%252Flogin%253F…` chain
 * observed in the wild.
 *
 * `pathname` here means the FIRST segment of the redirect value so
 * `/login?redirect=/quotations` (target = `/quotations`) is allowed
 * while `/login?redirect=/login?…` (target = `/login`) is rejected.
 */
function safeRedirectTarget(raw: string | null | undefined): string {
  if (!raw) return '/';
  if (!raw.startsWith('/')) return '/';
  if (raw.startsWith('//')) return '/';
  // Strip query/fragment and inspect only the path. Any /login target
  // (whether bare `/login` or `/login?…`) loops the user back here.
  const pathOnly = raw.split('?')[0]?.split('#')[0] ?? '';
  if (pathOnly === '/login' || pathOnly.startsWith('/login/')) {
    return '/';
  }
  return raw;
}
